import styled, { css, keyframes } from "styled-components";

const shimmerSweep = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

const loadingTextShimmer = css`
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.4) 0%,
    rgba(255, 255, 255, 0.4) 35%,
    #fff 50%,
    rgba(255, 255, 255, 0.4) 65%,
    rgba(255, 255, 255, 0.4) 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

const loadingMessageShimmer = css`
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.78) 0%,
    rgba(255, 255, 255, 0.78) 35%,
    #fff 50%,
    rgba(255, 255, 255, 0.78) 65%,
    rgba(255, 255, 255, 0.78) 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

// ─── Layout containers ───────────────────────────────────────────────────────

export const CollectionContainer = styled.div`
  width: 100%;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  transition: opacity 0.3s ease;
  contain: layout style paint;
  will-change: transform;
  aspect-ratio: ${(props) => props.$aspectRatio};
  cursor: ${(props) => (props.$clickable ? "pointer" : "not-allowed")};
  pointer-events: ${(props) => (props.$clickable ? "auto" : "none")};
`;

// ─── Media elements ──────────────────────────────────────────────────────────

export const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${(props) => (props.$loaded ? 1 : 0)};
  visibility: ${(props) => (props.$loaded ? "visible" : "hidden")};
  pointer-events: ${(props) => (props.$loaded ? "auto" : "none")};
  transition: opacity 0.4s ease;
  z-index: 1;
  border-radius: 12px;
  content-visibility: auto;
  will-change: opacity;
  transform: translateZ(0);
  backface-visibility: hidden;
`;

export const ImageElement = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.4s ease;
  border-radius: 12px;
`;

export const CollectionThumbnail = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 12px;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${(props) => (props.$loaded ? 0 : 1)};
  visibility: ${(props) => (props.$loaded ? "hidden" : "visible")};
  filter: ${(props) => (props.$blur ? "blur(35px)" : "none")};
  transform: ${(props) => (props.$blur ? "scale(1.1)" : "scale(1)")};
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  content-visibility: auto;
  will-change: opacity, transform;
  backface-visibility: hidden;
`;

// ─── Overlays ────────────────────────────────────────────────────────────────

export const IconWrapper = styled.div`
  position: absolute;
  left: 16px;
  top: 16px;
  z-index: 100;
  opacity: 0.6;
  svg {
    filter: drop-shadow(0px 0px 5px rgba(0, 0, 0, 0.5));
  }
`;

export const CheckboxContainer = styled.div`
  display: flex;
  padding: 6px;
  justify-content: center;
  align-items: center;
  border-radius: 100px;
  background: rgba(0, 0, 0, 0.5);
  position: absolute;
  right: 15px;
  top: 15px;
  gap: 10px;
  max-width: ${(p) => (p.$small ? "32px" : "unset")};
  max-height: ${(p) => (p.$small ? "32px" : "unset")};
  z-index: 6;
  pointer-events: auto;
`;

// ─── Footer ──────────────────────────────────────────────────────────────────

export const VideoFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: none;
  color: white;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 61.05%,
    rgba(0, 0, 0, 0.64) 100%
  );
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px;
  justify-content: flex-end;
  gap: 4px;
  transition: all 0.3s ease-in-out;
  will-change: opacity, transform;
`;

export const ModelName = styled.div`
  color: #fff;
  font-family: Figtree;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.07px;
  white-space: nowrap;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Date = styled.div`
  color: #fff;
  font-family: Figtree;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -1px;
  white-space: nowrap;
  opacity: 0.5;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ─── Loading / in-progress ───────────────────────────────────────────────────
export const LoadingImage = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  object-fit: cover;
  z-index: 0;
  filter: blur(8px) brightness(0.7) saturate(1.1);
  transform: scale(1.1);
`;
export const LoadingSpinnerBlur = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) translateZ(0);
  display: ${(props) => (props.$show ? "flex" : "none")};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 5;
  height: 100%;
  width: 100%;
  gap: 12px;
  will-change: opacity;

  &::after {
    content: "";
    position: absolute;
    z-index: 0;
    inset: 0;
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.5) 0%,
      rgba(0, 0, 0, 0) 100%
    );
  }

  l-line-spinner {
    --spinner-color: white;
  }
`;

export const RunningText = styled.div`
  color: white;
  text-align: center;
  z-index: 1;
  padding: 0 20px;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
`;

export const RunningTitle = styled.div`
  color: #fafafa;
  font-family: Figtree;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: -0.04px;
  margin-bottom: 6px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: baseline;
  white-space: nowrap;
`;

export const DotsSpan = styled.span`
  display: inline-block;
  flex: 0 0 11.8px;
  min-width: 11.8px;
  text-align: left;
`;

const shimmerTextIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(3px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/*
 * Safari disables background-clip: text when the clipped element or any of
 * its descendants is composited (filter, animated transform/opacity), which
 * leaves the transparent-filled text invisible. Keep the clip + sweep on
 * ShimmerTitle only, and put the drop-shadow and enter animation on this
 * wrapper above it.
 */
export const ShimmerTitleWrapper = styled.div`
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6));
  animation: ${shimmerTextIn} 0.6s ease;
`;

export const ShimmerTitle = styled.div`
  font-family: Figtree;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.3px;
  line-height: 1.4;
  margin-bottom: 6px;
  display: inline-block;
  max-width: 100%;
  min-width: 0;
  text-wrap: balance;
  ${loadingTextShimmer}
  animation: ${shimmerSweep} 3.8s linear infinite;

  @media (min-width: 366px) and (max-width: 768px) {
    font-size: 16px;
    font-weight: 650;
  }
`;

export const ShimmerText = styled.span`
  display: inline;
`;

export const RunningMessage = styled.div`
  font-family: Figtree;
  font-size: 12px;
  font-weight: 500;
  opacity: 0.4;
  line-height: 1.7;
  letter-spacing: -0.04px;
  ${loadingMessageShimmer}

  @media (min-width: 366px) and (max-width: 768px) {
    font-size: 14px;
  }
`;

export const FreeMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

// ─── Buttons ─────────────────────────────────────────────────────────────────

export const UpgradeButton = styled.button`
  border-radius: 100px;
  border: 0.5px solid #fff;
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.2) 100%), #fff;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.12);
  width: min-content;
  display: flex;
  height: 40px;
  padding: 12px 32px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  align-self: center;
  position: relative;
  overflow: hidden;
  margin-top: ${(props) => (props.$spacing ? "20px" : "10px")};
  color: #081120;
  font-family: Figtree;
  font-size: 16px;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: 0.08px;
  cursor: pointer;
  pointer-events: auto;
  transition: all 0.3s ease-in-out;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.49) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: buttonShimmer 4s infinite;
    pointer-events: none;
  }

  @keyframes buttonShimmer {
    0% {
      left: -100%;
    }
    100% {
      left: 150%;
    }
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(255, 255, 255, 0.1);
  }
`;

export const RetryButton = styled(UpgradeButton)``;

// ─── Failed state ────────────────────────────────────────────────────────────

export const FailedOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 5;
  height: 100%;
  width: 100%;
  gap: 8px;

  &::after {
    content: "";
    position: absolute;
    z-index: 0;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
  }
`;

export const FailedContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
  gap: 12px;
  padding: 16px;
`;

export const FailedIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 71, 87, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const FailedModelName = styled.div`
  font-family: Figtree;
  opacity: 0.4;
  color: #fff;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.08px;
`;

export const FailedTitle = styled.div`
  font-family: Figtree;
  color: #fff;
  text-align: center;
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.1px;
`;

export const FailedMessage = styled.div`
  font-family: Figtree;
  color: #fff;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  opacity: 0.78;
  letter-spacing: 0.07px;
`;
